import { Controller } from '@hotwired/stimulus';
import i18n from '../init/i18n';

export default class extends Controller {
  static targets = [
    'title',
    'status',
    'cancel',
    'close',
    'rows',
    'rowTemplate',
    'errorTemplate',
    'importedTemplate'
  ];

  initialize() {
    this.resources = new Map();
    this.cancelled = false;
  }

  open(resources) {
    this.cancelled = false;
    this.rowsTarget.replaceChildren();
    this.resources = new Map();
    // Mirror the records table order (DataroomRecord.tree_order)
    this.#sortLikeTable(resources).forEach((resource) => {
      const row = this.#appendRow(resource);
      this.resources.set(resource.id, { ...resource, done: 0, failed: 0, imported: [], errors: [], row });
    });

    this.titleTarget.textContent = i18n.t('dataroom_dropzone.import_window.title', { count: resources.length });
    this.statusTarget.textContent = i18n.t('dataroom_dropzone.import_window.in_progress');
    this.cancelTarget.classList.remove('d-none');
    this.closeTarget.classList.add('d-none');

    this.element.classList.remove('d-none');
  }

  startResource(id) {
    if (this.cancelled) return;

    const resource = this.resources.get(id);
    if (!resource || resource.errored || resource.done >= resource.total) return;

    this.#setRowState(resource.row, 'loading');
  }

  completeFile(id, fileLabel) {
    if (this.cancelled) return;

    const resource = this.resources.get(id);
    if (!resource) return;

    resource.done += 1;
    resource.imported.push(fileLabel || resource.name);
    this.#updateFolderCount(resource);
    this.#checkComplete(resource);
  }

  // A file inside a folder maps to the folder resource: we don't fail the folder
  // right away, we count the failure and let the row keep progressing — the files
  // that went through are imported all the same. Failures are listed one by one
  // once every child has been processed (see #checkComplete).
  failResource(id, message, fileLabel) {
    if (this.cancelled) return;

    const resource = this.resources.get(id);
    if (!resource) return;

    resource.failed += 1;
    resource.errors.push({
      label: fileLabel || resource.name,
      message: message || i18n.t('dataroom_dropzone.import_window.error_generic')
    });

    this.#checkComplete(resource);
  }

  markDone() {
    const failed = this.#failedCount();

    this.statusTarget.textContent = failed > 0
      ? i18n.t('dataroom_dropzone.import_window.done_with_errors', { count: failed })
      : i18n.t('dataroom_dropzone.import_window.done');
    this.cancelTarget.classList.add('d-none');
    this.closeTarget.classList.remove('d-none');
  }

  markCancelled() {
    this.cancelled = true;
    this.statusTarget.textContent = i18n.t('dataroom_dropzone.import_window.cancelled');
    this.cancelTarget.classList.add('d-none');
    this.closeTarget.classList.remove('d-none');

    this.resources.forEach((resource) => {
      if (resource.errored || resource.done >= resource.total) return;

      resource.row.querySelector('[data-count]').classList.add('d-none');
      this.#setRowState(resource.row, 'cancelled');
    });
  }

  toggleErrors(event) {
    const button = event.currentTarget;
    const errors = button.closest('[data-errors]');
    const expanded = button.getAttribute('aria-expanded') === 'true';

    button.setAttribute('aria-expanded', String(!expanded));
    errors.querySelector('[data-errors-list]').classList.toggle('d-none', expanded);
    button.querySelectorAll('[data-chevron-state]').forEach((chevron) => {
      chevron.classList.toggle('d-none', chevron.dataset.chevronState === (expanded ? 'expanded' : 'collapsed'));
    });
  }

  close() {
    this.element.classList.add('d-none');
  }

  // private

  #sortLikeTable(resources) {
    return [...resources].sort((a, b) => {
      if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }

  #appendRow({ name, isDirectory, total, merged }) {
    const row = this.rowTemplateTarget.content.cloneNode(true);
    row.querySelector('[data-name]').textContent = name;

    if (isDirectory) {
      row.querySelector('[data-folder-icon]').classList.remove('d-none');

      const count = row.querySelector('[data-count]');
      count.textContent = i18n.t('dataroom_dropzone.import_window.folder_count', { done: 0, total });
      count.classList.remove('d-none');

      if (merged) row.querySelector('[data-merge-note]').classList.remove('d-none');
    }

    this.rowsTarget.append(row);
    return this.rowsTarget.lastElementChild;
  }

  #updateFolderCount(resource) {
    if (!resource.isDirectory) return;

    resource.row.querySelector('[data-count]').textContent = i18n.t('dataroom_dropzone.import_window.folder_count', {
      done: resource.done,
      total: resource.total
    });
  }

  #checkComplete(resource) {
    if (resource.errored || resource.done + resource.failed < resource.total) return;

    if (resource.failed === 0) {
      this.#setRowState(resource.row, 'done');
    } else {
      this.#renderErrors(resource);
    }

    // A folder with partial failures still holds the files that went through:
    // it is worth opening, so it keeps its link to the records table.
    if (resource.done > 0) this.#linkFolder(resource);
  }

  // A Turbo stream inserts its content after a repaint: a folder created by the
  // last import of its batch is not in the table yet when its row completes, so
  // the lookup waits for the next frame.
  #linkFolder(resource) {
    if (!resource.isDirectory) return;

    requestAnimationFrame(() => {
      const folderLink = this.element
        .closest('turbo-frame')
        ?.querySelector(`[data-existing-folder-name="${CSS.escape(resource.name)}"] a[href]`);
      if (!folderLink) return;

      resource.row.querySelector('[data-link]').href = folderLink.getAttribute('href');
    });
  }

  #renderErrors(resource) {
    resource.errored = true;

    if (resource.isDirectory) {
      this.#renderFolderDetails(resource);
      // The folder row keeps its "done / total" count: the files that went through
      // are imported, only the listed ones are missing — hence a warning, and the
      // error state only when nothing at all could be imported.
      this.#setRowState(resource.row, resource.done > 0 ? 'warning' : 'error');
    } else {
      this.#renderFileError(resource);
      this.#setRowState(resource.row, 'error');
    }
  }

  // A dropped file carries its own name on the row: only its message is missing.
  #renderFileError(resource) {
    const message = resource.row.querySelector('[data-error-message]');

    message.textContent = resource.errors[0].message;
    message.classList.remove('d-none');
    resource.row.classList.add('bg-danger-100');
  }

  // The content of a folder is detailed file by file, behind a summary the user can
  // fold back once read: the files that failed first, then the ones that went through.
  #renderFolderDetails(resource) {
    const errors = resource.row.querySelector('[data-errors]');
    const list = errors.querySelector('[data-errors-list]');

    resource.errors.forEach(({ label, message }) => {
      const entry = this.errorTemplateTarget.content.cloneNode(true);

      entry.querySelector('[data-failed-name]').textContent = label;
      entry.querySelector('[data-failed-message]').textContent = message;
      list.append(entry);
    });

    [...resource.imported].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .forEach((label) => {
        const entry = this.importedTemplateTarget.content.cloneNode(true);

        entry.querySelector('[data-imported-name]').textContent = label;
        list.append(entry);
      });

    errors.querySelector('[data-errors-summary]').textContent = i18n.t(
      'dataroom_dropzone.import_window.failed_files',
      { count: resource.errors.length }
    );
    errors.classList.remove('d-none');
  }

  #failedCount() {
    let failed = 0;

    this.resources.forEach((resource) => { failed += resource.failed; });

    return failed;
  }

  #setRowState(row, state) {
    row.querySelectorAll('[data-loader-state]').forEach((el) => {
      el.classList.toggle('d-none', el.dataset.loaderState !== state);
    });

    row.querySelector('[data-name]').classList.toggle('text-gray-500', state === 'cancelled');
  }
}
